// Todo: Remove this comments (put to docs?)
// modal reqs
//   x looks the same as design in figma
//   x it's not necesary to create new service for new modal type
//   x it's possible to use Component and to pass it custom params
//   x not possible to interact with the app when modal is open (backdrop has pointer-events: none)
//   - better typings

import { Injectable, Injector, Type } from '@angular/core';
import { TuiPopover, TuiPopoverService } from '@taiga-ui/cdk';
import { TUI_DIALOGS, TuiDialogComponent, TuiDialogOptions } from '@taiga-ui/core';
import { PolymorpheusComponent, PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { endWith, ignoreElements, Observable } from 'rxjs';

import { AlertComponent, AlertOptions } from '../alert';
import { NoQuitPossibleDialogComponent } from '../no-quit-possible-dialog/no-quit-possible-dialog.component';

// Extended dialog options that allow custom data types
export type ExtendedDialogOptions<T = unknown> = Omit<TuiDialogOptions<void>, 'data'> & {
  data?: T;
};

@Injectable({ providedIn: 'root' })
export class ModalService extends TuiPopoverService<TuiDialogOptions<unknown>> {
  constructor() {
    super(TUI_DIALOGS, TuiDialogComponent, {
      size: 'm',
      required: false,
      closeable: true,
      dismissible: false,
      label: '',
      header: '',
      data: {},
      appearance: '',
    } as TuiDialogOptions<unknown>);
  }

  // Override open method to support custom data types
  override open<O = unknown, I = unknown>(
    content: PolymorpheusContent<TuiPopover<TuiDialogOptions<I>, O>>,
    options?: Partial<ExtendedDialogOptions<I>>,
  ): Observable<O> {
    // Cast is necessary because we're extending the base type to support arbitrary data
    return super.open(content, options as Partial<TuiDialogOptions<unknown>>);
  }

  openComponent<T, R>(
    Comp: Type<object>,
    options?: Partial<ExtendedDialogOptions<T>>,
    injector?: Injector,
  ): Observable<R> {
    if (injector) {
      return this.open(new PolymorpheusComponent(Comp, injector), options);
    }
    return this.open(new PolymorpheusComponent(Comp), options);
  }

  /**
   * Opens an informative dialog with a single "OK" button. Returned observable
   * emits and completes immediately after the dialog is acknowledged or closed.
   */
  openAlert(data: AlertOptions): Observable<void> {
    return this.openComponent(AlertComponent, { data, dismissible: false, size: 's' }).pipe(
      ignoreElements(),
      endWith(undefined),
    );
  }

  /**
   * Opens a dialog to confirm an abort of an "edit process".
   */
  openNoQuitPossibleDialog() {
    return this.openComponent(NoQuitPossibleDialogComponent, { dismissible: false });
  }
}

export type ModalContext<I, O> = TuiPopover<TuiDialogOptions<I>, O>;
